import { Feather } from "@expo/vector-icons";
import { RsvpEventDetails } from "queries/rsvp.queries";
import React, { useEffect, useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useRsvpColors } from "../theme";
import RsvpEventHeader from "./RsvpEventHeader";
import RsvpEventPicker from "./RsvpEventPicker";
import RsvpPolicyModal from "./RsvpPolicyModal";
import RsvpPrimaryButton from "./RsvpPrimaryButton";
import RsvpQuantityStepper from "./RsvpQuantityStepper";

interface Props {
  events: RsvpEventDetails[];
  preSelectedEventId?: number | null;
  // Tenant's RSVP disclaimer text, shown above the confirm button
  disclaimer?: string | null;
  // Tenant's cancellation policy: the title renders as a link that opens the
  // full body in a popup
  cancellationPolicyTitle?: string | null;
  cancellationPolicyBody?: string | null;
  onConfirm: (
    eventId: number,
    quantity: number,
    policyAccepted: boolean,
  ) => void;
  isConfirming?: boolean;
}

const RsvpEligibleView: React.FC<Props> = ({
  events,
  preSelectedEventId,
  disclaimer,
  cancellationPolicyTitle,
  cancellationPolicyBody,
  onConfirm,
  isConfirming,
}) => {
  const colors = useRsvpColors();
  const [selectedEventId, setSelectedEventId] = useState<number | null>(
    preSelectedEventId ?? (events.length === 1 ? events[0].eventId : null),
  );
  const [quantity, setQuantity] = useState(1);
  const [policyVisible, setPolicyVisible] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);

  const hasCancellationPolicy =
    !!cancellationPolicyTitle && !!cancellationPolicyBody;

  const selectedEvent = useMemo(
    () => events.find((event) => event.eventId === selectedEventId),
    [events, selectedEventId],
  );

  const joiningWaitlist = !!selectedEvent?.waitlisting;
  // Capacity reached on an event that does not waitlist: nothing to submit
  const isFull = !!selectedEvent?.rsvpFull;

  // Events sharing a slug may have different per-event maxes; bound the
  // stepper by the selected event's max (null = no cap, or no event selected yet)
  const effectiveMaxQuantity = selectedEvent?.maxQuantity ?? null;

  // Clamp a previously chosen quantity when switching to an event with a lower max
  useEffect(() => {
    if (effectiveMaxQuantity != null && quantity > effectiveMaxQuantity) {
      setQuantity(Math.max(1, effectiveMaxQuantity));
    }
  }, [effectiveMaxQuantity, quantity]);

  // Agreeing to the policy before picking a date: flag the picker so the
  // player knows why the confirm button is still disabled.
  const eventSelectionError =
    policyAccepted && selectedEventId == null
      ? "Must select an event date"
      : null;

  const handleConfirm = () => {
    if (selectedEventId == null) return;
    onConfirm(selectedEventId, quantity, policyAccepted);
  };

  const header =
    events.length === 1 ? (
      <RsvpEventHeader event={events[0]} />
    ) : (
      <>
        {/* Same-named events: show the shared name once; each picker tile
            carries only its date */}
        <RsvpEventHeader event={events[0]} showDate={false} />
        <RsvpEventPicker
          events={events}
          modelValue={selectedEventId}
          onChange={setSelectedEventId}
          error={eventSelectionError}
        />
      </>
    );

  // Capacity reached on an event that does not waitlist: nothing to submit,
  // so show a notice in place of the RSVP form.
  if (isFull) {
    return (
      <View>
        {header}
        <View
          style={{
            backgroundColor: colors.cardBg,
            borderWidth: 1,
            borderColor: colors.cardBorder,
            borderRadius: 16,
            padding: 24,
            alignItems: "center",
          }}
        >
          <Feather
            name="slash"
            size={40}
            color={colors.textMuted}
            style={{ marginBottom: 12 }}
          />
          <Text
            style={{
              color: colors.textSecondary,
              fontSize: 22,
              fontWeight: "700",
              textAlign: "center",
              marginBottom: 12,
            }}
          >
            This event is full
          </Text>
          <Text
            style={{
              color: colors.textSecondary,
              fontSize: 16,
              textAlign: "center",
              lineHeight: 24,
            }}
          >
            RSVP capacity has been reached and this event is not taking a
            waitlist.
            {events.length > 1 ? " Select another date above." : ""}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View>
      {header}

      <View
        style={{
          backgroundColor: colors.cardBg,
          borderWidth: 1,
          borderColor: joiningWaitlist ? colors.accent : colors.cardBorder,
          borderRadius: 16,
          padding: 24,
        }}
      >
        {joiningWaitlist ? (
          <View style={{ alignItems: "center", marginBottom: 24 }}>
            <Feather
              name="users"
              size={40}
              color={colors.accent}
              style={{ marginBottom: 12 }}
            />
            <Text
              style={{
                color: colors.accent,
                fontSize: 22,
                fontWeight: "700",
                textAlign: "center",
                marginBottom: 12,
              }}
            >
              You're joining the waitlist
            </Text>
            <Text
              style={{
                color: colors.textSecondary,
                fontSize: 16,
                textAlign: "center",
                lineHeight: 24,
              }}
            >
              This event is currently full.{"\n"}
              Add your name to the waitlist and we'll notify you if a spot
              opens up.
            </Text>
            <View
              style={{
                alignSelf: "stretch",
                height: 1,
                backgroundColor: colors.cardBorder,
                marginTop: 24,
              }}
            />
          </View>
        ) : (
          <Text
            style={{
              color: colors.textSecondary,
              fontSize: 16,
              textAlign: "center",
              marginBottom: 24,
            }}
          >
            You're eligible to RSVP for this event. Tap below to reserve your
            spot.
          </Text>
        )}

        <View style={{ alignItems: "center", marginBottom: 24 }}>
          <Text
            style={{
              color: joiningWaitlist ? colors.accent : colors.textMuted,
              fontSize: joiningWaitlist ? 16 : 14,
              fontWeight: joiningWaitlist ? "700" : "500",
              marginBottom: 12,
            }}
          >
            How many seats?
          </Text>
          <RsvpQuantityStepper
            quantity={quantity}
            onChange={setQuantity}
            maxQuantity={effectiveMaxQuantity}
          />
        </View>

        {joiningWaitlist ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              borderWidth: 1,
              borderColor: colors.cardBorder,
              borderRadius: 12,
              padding: 14,
              marginBottom: 16,
            }}
          >
            <Feather name="info" size={18} color={colors.textMuted} />
            <Text
              style={{
                flex: 1,
                color: colors.textSecondary,
                fontSize: 14,
                lineHeight: 20,
              }}
            >
              Joining the waitlist does not guarantee tickets. You will receive
              a notification if seats become available.
            </Text>
          </View>
        ) : null}

        {disclaimer ? (
          <Text
            style={{
              color: colors.textSecondary,
              fontSize: 9,
              lineHeight: 14,
              marginBottom: 16,
            }}
          >
            {disclaimer}
          </Text>
        ) : null}

        {hasCancellationPolicy ? (
          <Pressable
            accessibilityRole="checkbox"
            accessibilityState={{ checked: policyAccepted }}
            onPress={() => setPolicyAccepted((accepted) => !accepted)}
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 10,
              marginBottom: 16,
            }}
            testID="accept-cancellation-policy"
          >
            <View
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                borderWidth: 2,
                borderColor: policyAccepted
                  ? colors.accent
                  : colors.inputBorder,
                backgroundColor: policyAccepted
                  ? colors.accent
                  : colors.inputBg,
                alignItems: "center",
                justifyContent: "center",
                marginTop: 1,
              }}
            >
              {policyAccepted ? (
                <Feather name="check" size={15} color={colors.onAccent} />
              ) : null}
            </View>
            <Text
              style={{
                flex: 1,
                color: colors.textSecondary,
                fontSize: 13,
                lineHeight: 20,
              }}
            >
              I have read and agree to the{" "}
              <Text
                onPress={() => setPolicyVisible(true)}
                style={{
                  color: colors.accent,
                  fontWeight: "600",
                  textDecorationLine: "underline",
                }}
                testID="open-cancellation-policy"
              >
                {cancellationPolicyTitle}
              </Text>
            </Text>
          </Pressable>
        ) : null}

        <RsvpPrimaryButton
          title={joiningWaitlist ? "Join Waitlist" : "Confirm RSVP"}
          icon={joiningWaitlist ? "users" : undefined}
          disabled={
            selectedEventId == null ||
            (hasCancellationPolicy && !policyAccepted)
          }
          loading={isConfirming}
          onPress={handleConfirm}
          testID="confirm-rsvp"
        />
        {joiningWaitlist ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              marginTop: 14,
            }}
          >
            <Feather name="clock" size={14} color={colors.textMuted} />
            <Text
              style={{
                color: colors.textMuted,
                fontSize: 13,
                textAlign: "center",
              }}
            >
              You'll be notified by{" "}
              <Text style={{ fontWeight: "700" }}>email</Text> and{" "}
              <Text style={{ fontWeight: "700" }}>text</Text> if a spot opens
              up.
            </Text>
          </View>
        ) : null}
        {!joiningWaitlist ? (
          <Text
            style={{
              color: colors.textFaint,
              fontSize: 12,
              textAlign: "center",
              marginTop: 12,
            }}
          >
            Complimentary — no payment required.
          </Text>
        ) : null}
      </View>

      {joiningWaitlist ? (
        <Text
          style={{
            color: colors.textFaint,
            fontSize: 12,
            textAlign: "center",
            marginTop: 12,
          }}
        >
          Complimentary — no payment required.
        </Text>
      ) : null}

      {hasCancellationPolicy ? (
        <RsvpPolicyModal
          visible={policyVisible}
          title={cancellationPolicyTitle!}
          body={cancellationPolicyBody!}
          onClose={() => setPolicyVisible(false)}
        />
      ) : null}
    </View>
  );
};

export default RsvpEligibleView;
